import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Save, Plus, Trash2, RefreshCw, ShoppingCart, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

export const MaterialListModal = ({ budgetId, onClose }) => {
    const [list, setList] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/budgets/${budgetId}/material-list`).then((r) => setList(r.data)).catch(() => {
            toast.error("Nepodařilo se načíst seznam materiálu");
            onClose();
        });
    }, [budgetId]); // eslint-disable-line

    if (!list) return null;

    const setItem = (idx, patch) => {
        const items = list.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
        setList({ ...list, items });
    };

    const addItem = () => setList({ ...list, items: [...list.items, { name: "", quantity: 1, unit: "ks", note: "", purchased: false }] });
    const removeItem = (idx) => setList({ ...list, items: list.items.filter((_, i) => i !== idx) });

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put(`/budgets/${budgetId}/material-list`, {
                items: list.items.filter((it) => it.name).map((it) => ({ ...it, quantity: Number(it.quantity) || 0 })),
            });
            setList(data);
            toast.success("Seznam materiálu uložen");
        } catch { toast.error("Uložení selhalo"); }
        finally { setBusy(false); }
    };

    const regenerate = async () => {
        if (!window.confirm("Znovu vygenerovat seznam z položek rozpočtu? Ručně přidané položky se smažou (zaškrtnuté nákupy se u shodných názvů zachovají).")) return;
        try {
            const { data } = await api.post(`/budgets/${budgetId}/material-list/regenerate`);
            setList(data);
            toast.success("Seznam znovu vygenerován z rozpočtu");
        } catch { toast.error("Akce selhala"); }
    };

    const bought = list.items.filter((it) => it.purchased).length;

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white border-2 border-slate-900 max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="material-list-modal">
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                    <div className="flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-orange-600" />
                        <h2 className="font-display text-lg font-bold">Objednávka materiálu</h2>
                        <span className="ml-2 text-xs font-bold uppercase tracking-wider px-2 py-0.5 bg-slate-900 text-orange-400" data-testid="material-progress">
                            {bought}/{list.items.length} koupeno
                        </span>
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                </div>

                <div className="overflow-y-auto flex-1 p-4">
                    {list.items.length === 0 && (
                        <div className="p-8 text-center text-slate-500 text-sm">Seznam je prázdný — přidejte položky nebo vygenerujte z rozpočtu.</div>
                    )}
                    {list.items.map((it, idx) => (
                        <div key={it.id || idx} className={`flex items-center gap-2 px-2 py-2 border-b border-slate-100 ${it.purchased ? "opacity-50" : ""}`}>
                            <button onClick={() => setItem(idx, { purchased: !it.purchased })} data-testid={`material-toggle-${idx}`}
                                    className="text-slate-400 hover:text-orange-600 shrink-0">
                                {it.purchased ? <CheckSquare className="w-5 h-5 text-emerald-600" /> : <Square className="w-5 h-5" />}
                            </button>
                            <input value={it.name} onChange={(e) => setItem(idx, { name: e.target.value })} placeholder="Název materiálu"
                                   className={`flex-1 px-2 py-1.5 border border-slate-200 text-sm rounded-sm focus:outline-none focus:border-orange-600 ${it.purchased ? "line-through" : ""}`} />
                            <input type="number" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })}
                                   className="w-20 px-2 py-1.5 border border-slate-200 text-sm text-right rounded-sm focus:outline-none focus:border-orange-600" />
                            <input value={it.unit} onChange={(e) => setItem(idx, { unit: e.target.value })}
                                   className="w-16 px-2 py-1.5 border border-slate-200 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            <input value={it.note || ""} onChange={(e) => setItem(idx, { note: e.target.value })} placeholder="Poznámka (dodavatel…)"
                                   className="w-40 px-2 py-1.5 border border-slate-200 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            <button onClick={() => removeItem(idx)} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                    <button onClick={addItem} data-testid="material-add-item"
                            className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-orange-700 hover:text-orange-800">
                        <Plus className="w-3.5 h-3.5" /> Přidat položku
                    </button>
                </div>

                <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200 justify-between">
                    <button onClick={regenerate} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">
                        <RefreshCw className="w-3.5 h-3.5" /> Z rozpočtu
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Zavřít</button>
                        <button onClick={save} disabled={busy} data-testid="material-save"
                                className="px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                            <Save className="w-3.5 h-3.5" /> Uložit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MaterialListModal;
